import React from 'react'
import * as PropTypes from 'prop-types'
import withNotifier from '@softwaredev_cegeka/stix-notifier/dist/components/withNotifier'
import localStorage from 'src/utils/localStorage'
import { CoreApi } from 'src/api/CoreApi'
import { EventsApi } from 'src/api/EventsApi'
import withPermissions from 'src/components/auth/withPermissions'
import withUserData from 'src/components/auth/withUserData'
import safeJsonParse from 'src/utils/safeJsonParse'

const EVENT_DATA = 'eventData'
const CORE_DATA = 'coreData'
/**
 * This sends notifications whenever a previously in-progress export has completed.
 * This might be a little over-engineered :)
 */
class ExportCompleteNotifier extends React.Component {
    static propTypes = {
        notifier: PropTypes.any,
        children: PropTypes.any,
        permissions: PropTypes.any,
    }

    stateKeys = [EVENT_DATA, CORE_DATA]
    state = {
        previouslyPendingExportIds: [],
        pollTimeoutRefs: {},
        loadingCore: false,
        loadingEvents: false,
        data: {
            coreData: [],
            eventData: [],
        },
    }
    POLL_INTERVAL = 30_000
    apiClients = {
        coreData: new CoreApi(10000),
        eventData: new EventsApi(1000),
    }
    permissions = {
        eventData: 'events:event:create-export',
        coreData: 'core:cmdb:create-export',
    }

    /**
     * This triggers when another tab updates localStorage
     * @param ev
     */
    onStorageUpdate = ev => {
        if (!ev.key.startsWith('ExportCompleteNotifier.')) {
            return
        }
        const { pollTimeoutRefs } = this.state
        const [, stateKey, field] = ev.key.split('.')
        // If another tab updated an already queued action, dequeue it and restart the timeout.
        if (pollTimeoutRefs[stateKey] && field === 'updatedAt') {
            clearTimeout(pollTimeoutRefs[stateKey])
            setTimeout(
                () => {
                    this.queueNextUpdate(stateKey)
                    // This is a random delay to prevent the events across tabs ending up in the same event loop cycle.
                    // If they were to "sync", we'd still do multiple API calls.
                },
                Math.floor(Math.random() * (750 - 250) + 250)
            )
        }
        if (field === 'done') {
            const doneIds = safeJsonParse(ev.newValue, null)
            if (doneIds?.length) {
                doneIds.forEach(this.createDoneNotification)
            }
        }
    }

    /**
     * Create a notification to the user when the export is finished.
     * If we ever need to add data about the notification here, we'll have to go fetch it as it isn't included in the data because we filtered by status !== done.
     * @param id
     */
    createDoneNotification = id => {
        this.props.notifier.addSuccessMessage('Your export is complete!', {
            message:
                'A download link will be sent to your mailbox. Maximum 3 concurrent exports allowed.',
            autohide: true,
            delay: 5000,
        })
    }

    /**
     * On mount, load initial data and bind to localStorage updates from other tabs/windows
     */
    componentDidMount() {
        this.stateKeys.forEach(stateKey => {
            if (this.isAllowed(stateKey)) {
                this.loadData(stateKey)
            }
        })
        window.addEventListener('storage', this.onStorageUpdate)
    }

    /**
     * On unmount, remove localStorage listener
     */
    componentWillUnmount() {
        window.removeEventListener('storage', this.onStorageUpdate)
    }

    /**
     * If we loaded data successfully, queue the next update cycle.
     * @param prevProps
     * @param prevState
     */
    componentDidUpdate(prevProps, prevState) {
        this.stateKeys.forEach(stateKey => {
            if (
                this.isAllowed(stateKey) &&
                prevState?.data[stateKey] !== this.state?.data[stateKey]
            ) {
                this.processUpdate(stateKey)
                this.queueNextUpdate(stateKey)
            }
        })
    }

    /**
     * Schedule the next update this.POLL_INTERVAL ms in the future.
     * We store the timeoutRef for clearing it in case another tab updates first.
     * @param stateKey
     */
    queueNextUpdate(stateKey) {
        const timeoutRef = setTimeout(() => {
            this.loadData(stateKey)
        }, this.POLL_INTERVAL)
        this.setState(state => ({
            pollTimeoutRefs: {
                ...state.pollTimeoutRefs,
                [stateKey]: timeoutRef,
            },
        }))
    }

    /**
     * Check which exports completed, and show a message.
     * Updates localStorage to notify other tabs of updates.
     * @param stateKey
     */
    processUpdate(stateKey) {
        const lsKey = `ExportCompleteNotifier.${stateKey}`
        const previousInProgress = safeJsonParse(
            localStorage.getItem(`${lsKey}.inProgress`),
            []
        )
        const inProgress = this.state.data[stateKey]?.map?.(result => {
            return result.id
        })
        const done = previousInProgress.filter(id => {
            return !inProgress?.includes(id)
        })

        done.forEach(this.createDoneNotification)
        localStorage.setItem(
            `${lsKey}.inProgress`,
            JSON.stringify(inProgress ?? [])
        )
        localStorage.setItem(`${lsKey}.done`, JSON.stringify(done ?? []))
        localStorage.setItem(
            `${lsKey}.updatedAt`,
            String(window.performance.now())
        )
    }

    isAllowed(stateKey) {
        return this.props.userData.permissions.includes(
            this.permissions[stateKey]
        )
    }

    loadData(stateKey) {
        return this.apiClients[stateKey]
            .fetchExportHistory({
                filter: {
                    status: {
                        op: '!=',
                        value: 'done',
                    },
                    document_requester: this.props.userData.data.email,
                },
            })
            .then(res => {
                this.setState(state => ({
                    data: {
                        ...state.data,
                        [stateKey]: res.data.results,
                    },
                }))
            })
    }

    render() {
        return null
    }
}

export function addEventExportInProgress(id) {
    const lsKey = `ExportCompleteNotifier.${EVENT_DATA}.inProgress`
    const inProgress = safeJsonParse(localStorage.getItem(lsKey), [])
    localStorage.setItem(lsKey, JSON.stringify([...inProgress, id] ?? []))
}

export function addCoreExportInProgress(id) {
    const lsKey = `ExportCompleteNotifier.${CORE_DATA}.inProgress`
    const inProgress = safeJsonParse(localStorage.getItem(lsKey), [])
    localStorage.setItem(lsKey, JSON.stringify([...inProgress, id] ?? []))
}

export default withPermissions(
    withUserData(withNotifier(ExportCompleteNotifier))
)
